import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { artistsApi, eventOsApi, eventsApi, venuesApi } from "../../api";
import EventFormFields, { type EventFormValues } from "../../components/events/EventFormFields";
import { workspaceNav } from "../../routes/workspaceNavigation";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { Artist, EventListItem, Venue } from "../../types";
import { artistDisplayName } from "../../utils/artistProfile";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { venuePrimaryCity } from "../../utils/venueAddress";
import { isPastDateKey } from "../../utils/calendarDates";
import {
  findVenueConflict,
  normalizeTicketTiers,
  ticketCapacityError,
  toIsoOrUndefined,
  venueConflictMessage,
  type TicketTierDraft,
} from "../../utils/eventForm";
import "./EventOnboardingPage.css";

type Props = {
  workspace: WorkspaceId;
};

function parsePrefillDate(raw: string | null): string {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "";
  const [y, m, d] = raw.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return "";
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return "";
  return isPastDateKey(raw) ? "" : raw;
}

const initialForm = (prefillDate = ""): EventFormValues => ({
  eventName: "",
  selectedArtistId: "",
  eventDate: prefillDate,
  startAt: "",
  endAt: "",
  venueId: "",
  eventCapacity: "",
  // FE-3 / BE-8: leave the first tier name blank so the placeholder
  // ("General Admission") is what guides the user. A hardcoded "General"
  // seeded a default the user had to manually delete on every event.
  ticketTiers: [{ id: crypto.randomUUID(), tier_name: "", price: "", quantity: "" }],
  productionCost: "",
  marketingCost: "",
  artistFee: "",
  status: "draft",
  holdLevel: "",
});

const EventOnboardingPage: React.FC<Props> = ({ workspace }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const prefillDate = useMemo(() => parsePrefillDate(searchParams.get("date")), [searchParams]);
  const [form, setForm] = useState<EventFormValues>(() => initialForm(prefillDate));

  const artistsQuery = useQuery({
    queryKey: token ? queryKeys.artists.list(workspace) : queryKeys.artists.all(workspace),
    queryFn: () => artistsApi.list(token!),
    enabled: Boolean(token),
  });

  const venuesQuery = useQuery({
    queryKey: token ? queryKeys.venues.list(workspace) : queryKeys.venues.all(workspace),
    queryFn: () => venuesApi.list(token!),
    enabled: Boolean(token),
  });

  useEffect(() => {
    if (!form.eventDate) return;
    if (!form.startAt) setForm((prev) => ({ ...prev, startAt: `${form.eventDate}T18:00` }));
    if (!form.endAt) setForm((prev) => ({ ...prev, endAt: `${form.eventDate}T23:00` }));
  }, [form.eventDate, form.endAt, form.startAt]);

  const artists = useMemo<Artist[]>(() => artistsQuery.data ?? [], [artistsQuery.data]);
  const venues = useMemo<Venue[]>(() => venuesQuery.data ?? [], [venuesQuery.data]);
  const selectedArtistName = useMemo(
    () => {
      const artist = artists.find((item) => item.id === form.selectedArtistId);
      return artist ? artistDisplayName(artist) : null;
    },
    [artists, form.selectedArtistId],
  );

  const patchForm = (patch: Partial<EventFormValues>) => setForm((prev) => ({ ...prev, ...patch }));

  const patchTier = (id: string, patch: Partial<TicketTierDraft>) => {
    setForm((prev) => ({
      ...prev,
      ticketTiers: prev.ticketTiers.map((tier) => (tier.id === id ? { ...tier, ...patch } : tier)),
    }));
  };

  const addTier = () => {
    setForm((prev) => ({
      ...prev,
      ticketTiers: [...prev.ticketTiers, { id: crypto.randomUUID(), tier_name: "", price: "", quantity: "" }],
    }));
  };

  const removeTier = (id: string) => {
    setForm((prev) =>
      prev.ticketTiers.length > 1 ? { ...prev, ticketTiers: prev.ticketTiers.filter((tier) => tier.id !== id) } : prev,
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Ref lock is synchronous — `submitting` state is too late to stop a
    // second click (or a slash-redirect replay) from issuing two POSTs.
    if (submitLock.current) return;
    submitLock.current = true;
    if (!token) {
      submitLock.current = false;
      toast.error("Session expired. Sign in again.");
      return;
    }

    const normalizedTiers = normalizeTicketTiers(form.ticketTiers);
    if (!normalizedTiers) {
      toast.error("Add at least one valid ticket tier.");
      submitLock.current = false;
      return;
    }

    if (!form.venueId) {
      toast.error("Select a venue — event location is taken from the venue address.");
      submitLock.current = false;
      return;
    }

    const venue = venues.find((item) => item.id === form.venueId);
    const city = venue ? venuePrimaryCity(venue) : "";
    if (!city) {
      toast.error("Selected venue has no city on file. Update the venue address in Venues registry.");
      submitLock.current = false;
      return;
    }

    // FE-5: catch obvious year/range errors before the round-trip. Backend
    // re-validates (BE-5) — these client checks just give faster feedback.
    const currentYear = new Date().getFullYear();
    const mainYear = form.eventDate ? new Date(form.eventDate).getFullYear() : currentYear;
    if (!form.eventDate || isPastDateKey(form.eventDate)) {
      toast.error("Event date cannot be in the past.");
      submitLock.current = false;
      return;
    }
    if (!Number.isFinite(mainYear) || mainYear < currentYear) {
      toast.error("Event date must be in the current year or later.");
      submitLock.current = false;
      return;
    }
    if (form.startAt && form.endAt && new Date(form.endAt) <= new Date(form.startAt)) {
      toast.error("Event end must be after the start date and time.");
      submitLock.current = false;
      return;
    }

    const capacityError = ticketCapacityError(form.ticketTiers, form.eventCapacity);
    if (capacityError) {
      toast.error(capacityError);
      submitLock.current = false;
      return;
    }

    setSubmitting(true);
    try {
      // Fetch fresh at submit time so a booking made a moment ago is still caught.
      let existingEvents: EventListItem[];
      try {
        existingEvents = await queryClient.fetchQuery({
          queryKey: queryKeys.events.list(workspace),
          queryFn: () => eventsApi.list(token),
          staleTime: 0,
        });
      } catch {
        toast.error("Could not check venue availability. Try again.");
        submitLock.current = false;
        return;
      }
      const conflict = findVenueConflict(existingEvents, {
        venueId: form.venueId,
        eventDate: form.eventDate,
        startAt: form.startAt,
        endAt: form.endAt,
      });
      if (conflict) {
        toast.error(venueConflictMessage(conflict));
        submitLock.current = false;
        return;
      }

      const created = await eventOsApi.createEvent(
        {
          name: form.eventName,
          artist: selectedArtistName,
          artist_id: form.selectedArtistId || null,
          city,
          venue_capacity: Number(form.eventCapacity),
          event_date: form.eventDate,
          start_date: toIsoOrUndefined(form.startAt) ?? toIsoOrUndefined(`${form.eventDate}T18:00`),
          end_date: toIsoOrUndefined(form.endAt) ?? toIsoOrUndefined(`${form.eventDate}T23:00`),
          venue_id: form.venueId,
          ticket_tiers: normalizedTiers,
          finances: {
            production_cost: Number(form.productionCost),
            marketing_cost: Number(form.marketingCost),
            artist_fee: Number(form.artistFee),
          },
        },
        token,
      );
      toast.success("Event created");
      await queryClient.invalidateQueries({ queryKey: queryKeys.events.all(workspace) });
      navigate(workspaceNav.eventOverview(workspace, created.id), { replace: true });
    } catch (err) {
      submitLock.current = false;
      toast.error(err instanceof Error ? err.message : "Failed to create event");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="event-onboarding-page event-onboarding-page--fit">
      <Link to={workspaceNav.events(workspace)} className="app-shell-back-link">
        ← Back to events
      </Link>
      <div className="eventos-onboarding-heading-row">
        <h1 className="app-shell-page-title eventos-onboarding-heading-title">Add an event</h1>
        <p className="app-shell-lede eventos-onboarding-heading-sub">
          Work through each block. Once created, the event opens on its overview so you can finish setting it up.
        </p>
      </div>

      <div className="eventos-card eventos-form-card-scroll eventos-onboarding-fullpage">
        <div className="eventos-label">Onboarding</div>
        <form
          onSubmit={handleSubmit}
          // FE-4: Pressing Enter inside any text/number input was triggering
          // the implicit form submit. If validation failed (e.g. missing
          // venue), the user perceived it as the form "resetting" — the
          // partially typed fields lost focus and the toast scrolled away.
          // Guard Enter on everything except <button>, <textarea>, and
          // <select> so the only way to submit is the explicit button.
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            const target = e.target as HTMLElement;
            const tag = target.tagName;
            if (tag === "BUTTON" || tag === "TEXTAREA" || tag === "SELECT") return;
            const inputType = (target as HTMLInputElement).type;
            if (inputType === "submit" || inputType === "button") return;
            e.preventDefault();
          }}
          className="eventos-form eventos-onboarding-fullpage-form event-onboarding-page__form"
        >
          <EventFormFields
            mode="create"
            artists={artists}
            venues={venues}
            values={form}
            onPatch={patchForm}
            onPatchTier={patchTier}
            onAddTier={addTier}
            onRemoveTier={removeTier}
            showStatus={false}
          />

          <div className="app-form-actions">
            <button className="eventos-primary-btn" type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create event"}
            </button>
            <Link
              to={workspaceNav.events(workspace)}
              className="eventos-nav-btn event-onboarding-page__cancel-link"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventOnboardingPage;
